import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Button, Divider } from '@material-ui/core';

import Can from 'components/Can';
import { Card } from 'components/UI';
import Loading from 'components/Loading';
import useStyles from './Comments.styled';
import CommentsList from './CommentsList';
import { TextareaField } from 'components/FormFields';
import { CommentsActions } from 'state/modules/comments';

const Comments = ({ id, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });
  const { loading, comments, pagination } = useSelector((state) => state.comments || {});
  const [comment, setComment] = useState('');

  /**
   * Handle onchange of the textarea
   * @param {object} e Value of the input
   */
  const handleChange = (e) => {
    setComment(e.target.value);
  };

  /**
   * Get comments
   * @param {object} params
   */
  const getComments = (params) => {
    const payload = {
      ...params,
      object_id: id,
      object_class: type,
    };
    dispatch(CommentsActions.fetchComments(payload));
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    getComments(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    getComments(newParams);
  };

  /**
   * Submit new comment
   * Added new coment and reset field
   */
  const addComment = () => {
    const payload = {
      object_class: type,
      object_id: id,
      body: comment,
    };
    const paramsList = {
      page: 1,
      object_id: id,
      object_class: type,
      limit: params.limit,
    };
    dispatch(CommentsActions.fetchAddComment(payload, paramsList));
    setComment('');
  };

  useEffect(() => {
    getComments(params);
  }, []); // eslint-disable-line

  return (
    <Card title="Comments" headerActionType="vertical" datacy="comments-widget">
      <Box className={classes.container}>
        <Can I="create" a="comment">
          <Box className={classes.formContainer} mb={3}>
            <TextareaField
              label="Write a comment"
              field={{
                value: comment,
                name: 'comment',
                onChange: handleChange,
              }}
              className={classes.textarea}
            />
            <Box display="flex" mt={1} justifyContent="flex-end">
              <Button
                color="primary"
                variant="contained"
                onClick={addComment}
                data-cy="save-comment-btn"
                disabled={!comment || loading}
                style={{
                  textTransform: 'capitalize',
                  fontWeight: 500,
                }}
              >
                Comment
              </Button>
            </Box>
          </Box>
          <Divider />
        </Can>
        <Can I="read" a="comment">
          {loading ? (
            <Box
              className={classes.loadingContainer}
              justifyContent="center"
              alignItems="center"
              display="flex"
            >
              <Loading />
            </Box>
          ) : (
            <>
              {comments.length > 0 && (
                <CommentsList
                  params={params}
                  comments={comments}
                  pagination={pagination}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </>
          )}
        </Can>
      </Box>
    </Card>
  );
};

Comments.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
};

export default Comments;
